import { CUSTOM_SIZE, formatSizeLabel, SIZE_PRESETS } from '@cowell/shared';
import { useState } from 'react';

import { useConfig } from '../state/hooks.js';
import { useConfigStore } from '../state/store.js';

function snapToIncrement(n: number): number {
  return Math.round(n / CUSTOM_SIZE.incrementInches) * CUSTOM_SIZE.incrementInches;
}

export function SizePicker() {
  const config = useConfig();
  const applyPreset = useConfigStore((s) => s.applyPreset);
  const applyCustomSize = useConfigStore((s) => s.applyCustomSize);

  const [customOpen, setCustomOpen] = useState(config.size.isCustom);
  // Default custom size: 5" × 2" — horizontal, a common patch aspect.
  const [customW, setCustomW] = useState(config.size.isCustom ? config.size.width : 5);
  const [customH, setCustomH] = useState(config.size.isCustom ? config.size.height : 2);

  const isSelectedPreset = (w: number, h: number) =>
    !config.size.isCustom && config.size.width === w && config.size.height === h;

  const commitCustom = (w: number, h: number) => {
    const snappedW = Math.max(
      CUSTOM_SIZE.minInches,
      Math.min(CUSTOM_SIZE.maxInches, snapToIncrement(w)),
    );
    const snappedH = Math.max(
      CUSTOM_SIZE.minInches,
      Math.min(CUSTOM_SIZE.maxInches, snapToIncrement(h)),
    );
    setCustomW(snappedW);
    setCustomH(snappedH);
    applyCustomSize(snappedW, snappedH);
  };

  return (
    <fieldset className="section">
      <legend className="section-legend">Size</legend>

      <div className="size-grid" role="radiogroup" aria-label="Preset sizes">
        {SIZE_PRESETS.map((p) => {
          const selected = isSelectedPreset(p.width, p.height);
          return (
            <button
              key={`${p.width}x${p.height}`}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`size-tile${selected ? ' size-tile--selected' : ''}`}
              onClick={() => {
                setCustomOpen(false);
                applyPreset(p);
              }}
            >
              <span className="size-tile__dims">
                {formatSizeLabel({ width: p.width, height: p.height, isCustom: false })}
              </span>
            </button>
          );
        })}

        <button
          type="button"
          role="radio"
          aria-checked={config.size.isCustom}
          className={`size-tile size-tile--custom${config.size.isCustom ? ' size-tile--selected' : ''}`}
          onClick={() => {
            setCustomOpen(true);
            commitCustom(customW, customH);
          }}
        >
          <span className="size-tile__dims">Custom</span>
        </button>
      </div>

      {customOpen ? (
        <div className="custom-size">
          <label className="custom-size__field">
            <span>Width</span>
            <input
              type="number"
              min={CUSTOM_SIZE.minInches}
              max={CUSTOM_SIZE.maxInches}
              step={CUSTOM_SIZE.incrementInches}
              value={customW}
              onChange={(e) => setCustomW(Number(e.target.value))}
              onBlur={() => commitCustom(customW, customH)}
            />
            <span className="unit">in</span>
          </label>
          <label className="custom-size__field">
            <span>Height</span>
            <input
              type="number"
              min={CUSTOM_SIZE.minInches}
              max={CUSTOM_SIZE.maxInches}
              step={CUSTOM_SIZE.incrementInches}
              value={customH}
              onChange={(e) => setCustomH(Number(e.target.value))}
              onBlur={() => commitCustom(customW, customH)}
            />
            <span className="unit">in</span>
          </label>
          <p className="hint">
            {CUSTOM_SIZE.minInches}"–{CUSTOM_SIZE.maxInches}" in{' '}
            {CUSTOM_SIZE.incrementInches}" increments. Custom size adds $
            {CUSTOM_SIZE.surchargePerPanel.toFixed(2)}/panel (waived at quantity ≥{' '}
            {CUSTOM_SIZE.waiverMinQuantity}).
          </p>
        </div>
      ) : null}
    </fieldset>
  );
}
